import type { FolderPathSegment } from "@/domains/documents/utils/folderPathResolver";
import type { LinkedDocument } from "@/domains/pm/types";

/** What an attachment's gallery tile shows; null keeps it a plain row. */
export type AttachmentPreviewKind = "image" | "pdf";

/**
 * Where a tile's picture comes from: a file in Storage, signed (and resized)
 * on demand, or one still on the user's computer, read from its object URL.
 */
export type AttachmentSource =
  | { type: "stored"; filePath: string }
  | { type: "local"; url: string };

/** The picture a gallery tile shows and the name it carries. */
export interface GalleryPicture {
  /** Unique within the gallery; what layout and failure reports key on. */
  key: string;
  filename: string;
  size: number | null;
  source: AttachmentSource;
  /** The file's folder path, shown on hover; absent for a file not in Franklin. */
  breadcrumbSegments?: FolderPathSegment[] | null;
}

/** A document already in Franklin, as a gallery picture keyed by its id. */
export function storedGalleryPicture(doc: LinkedDocument, key = String(doc.id)): GalleryPicture {
  return {
    key,
    filename: doc.filename,
    size: doc.size,
    source: { type: "stored", filePath: doc.file_path },
    breadcrumbSegments: doc.breadcrumbSegments,
  };
}

type PreviewSource = Pick<LinkedDocument, "filename" | "content_type" | "size">;

/** Formats Supabase Storage can resize into a WebP thumbnail — HEIC and TIFF
 *  included, which is the only way a browser gets to show them. */
const TRANSFORMABLE_IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "webp",
  "avif",
  "gif",
  "ico",
  "svg",
  "bmp",
  "tif",
  "tiff",
  "heic",
]);

/** Formats an `<img>` can show untransformed — the fallback when Storage
 *  cannot resize the file. */
const BROWSER_IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "webp",
  "avif",
  "gif",
  "ico",
  "svg",
  "bmp",
]);

/** Storage refuses to transform anything larger. */
export const MAX_TRANSFORM_BYTES = 25 * 1024 * 1024;

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : "";
}

export function attachmentPreviewKind(doc: PreviewSource): AttachmentPreviewKind | null {
  const ext = extensionOf(doc.filename);
  if (ext === "pdf" || doc.content_type === "application/pdf") return "pdf";
  if (TRANSFORMABLE_IMAGE_EXTENSIONS.has(ext)) return "image";
  return null;
}

/** Whether Storage can serve this image resized. */
export function canTransformImage(doc: Pick<PreviewSource, "filename" | "size">): boolean {
  if (!TRANSFORMABLE_IMAGE_EXTENSIONS.has(extensionOf(doc.filename))) return false;
  return doc.size == null || doc.size <= MAX_TRANSFORM_BYTES;
}

/** Whether a browser can show this image from its original bytes. */
export function browserCanShowImage(doc: Pick<PreviewSource, "filename">): boolean {
  return BROWSER_IMAGE_EXTENSIONS.has(extensionOf(doc.filename));
}

/** Rendered sizes, in pixels, a tile asks for — stepped, so a panel being
 *  resized asks for a handful of thumbnails rather than one per pixel. Storage
 *  caps a transform at 2500. */
const PREVIEW_WIDTH_STEPS: readonly number[] = [160, 320, 480, 640, 960, 1280, 1920, 2500];

const MAX_PREVIEW_WIDTH = 2500;

/** The smallest step that covers `width`; the largest when none does. */
export function previewWidthStep(width: number): number {
  return PREVIEW_WIDTH_STEPS.find((step) => step >= width) ?? MAX_PREVIEW_WIDTH;
}

/** Placeholder aspect ratios until the real one is known. */
export const DEFAULT_ASPECT_RATIO: Record<AttachmentPreviewKind, number> = {
  image: 4 / 3,
  // A4 portrait.
  pdf: 595 / 842,
};
